import { InboxOutlined } from '@ant-design/icons';
import type { SmartBillImportPreview, SmartBillPreviewRow } from '@financiar/shared';
import { useMutation } from '@tanstack/react-query';
import { Alert, App, Button, Checkbox, Modal, Spin, Table, Tag, Upload } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { RcFile } from 'antd/es/upload';
import { useState } from 'react';
import { getApiError } from '../../api/client';
import { DateText, MoneyText } from '../../lib/format';
import { commitSmartbillImport, previewSmartbillImport } from './api';

const { Dragger } = Upload;

type ImportPhase = 'upload' | 'preview' | 'importing';

/**
 * SmartBill "Raport documente furnizor" import — the historic backfill for
 * invoices older than SPV's ~60-day window. Same non-interactive shape as
 * `SagaImportModal`, with ONE crucial difference: the report can't identify
 * the firm, so the currently selected one is the explicit target and the
 * preview banner names it — read it before committing.
 */
export function SmartBillImportModal({
  open,
  firmaId,
  onClose,
  onDone,
}: {
  open: boolean;
  firmaId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { message } = App.useApp();
  const [phase, setPhase] = useState<ImportPhase>('upload');
  const [preview, setPreview] = useState<SmartBillImportPreview | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [markAllPaid, setMarkAllPaid] = useState(false);

  function resetToUpload() {
    setPhase('upload');
    setPreview(null);
    setFile(null);
    setMarkAllPaid(false);
  }

  function handleClose() {
    resetToUpload();
    onClose();
  }

  const previewMutation = useMutation({
    mutationFn: (uploaded: File) => previewSmartbillImport(firmaId, uploaded),
    onSuccess: (data, uploaded) => {
      setPreview(data);
      setFile(uploaded);
      setPhase('preview');
    },
    onError: (error) => {
      message.error(getApiError(error)?.message ?? 'Fișierul nu a putut fi analizat.');
    },
  });

  const commitMutation = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Fișierul lipsește.');
      return commitSmartbillImport(firmaId, file, { markAllPaid });
    },
    onMutate: () => setPhase('importing'),
    onSuccess: (result) => {
      const parts = [`${result.expensesCreated} cheltuiel${result.expensesCreated === 1 ? 'ă importată' : 'i importate'}`];
      if (result.suppliersCreated > 0) parts.push(`${result.suppliersCreated} furnizor${result.suppliersCreated === 1 ? '' : 'i'} noi`);
      if (result.categoriesCreated > 0) parts.push(`${result.categoriesCreated} categori${result.categoriesCreated === 1 ? 'e' : 'i'} noi`);
      if (result.skippedDuplicates > 0) parts.push(`${result.skippedDuplicates} sărite (duplicate)`);
      if (result.failed > 0) parts.push(`${result.failed} eșuate`);
      message.success(`${parts.join(', ')}.`);
      onDone();
      handleClose();
    },
    onError: (error) => {
      setPhase('preview');
      message.error(getApiError(error)?.message ?? 'Importul a eșuat.');
    },
  });

  const columns: ColumnsType<SmartBillPreviewRow> = [
    { title: 'Document', dataIndex: 'docLabel', width: 160, ellipsis: true },
    { title: 'Furnizor', dataIndex: 'supplierName', ellipsis: true },
    { title: 'Dată', key: 'docDate', width: 100, render: (_, row) => <DateText value={row.docDate} /> },
    { title: 'Categorie', dataIndex: 'categoryName', width: 170, ellipsis: true, render: (value: string | null) => value ?? '—' },
    {
      title: 'Total',
      key: 'total',
      width: 130,
      align: 'right',
      render: (_, row) => <MoneyText value={row.totalAmount} currency={row.currency} />,
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_, row) =>
        row.duplicate ? (
          <Tag color="default">Duplicat</Tag>
        ) : row.paid || markAllPaid ? (
          <Tag color="green">Plătită</Tag>
        ) : (
          <Tag color="blue">Neplătită</Tag>
        ),
    },
  ];

  const importable = preview ? preview.summary.rows - preview.summary.duplicates : 0;

  return (
    <Modal
      title="Importă istoric SmartBill (documente furnizor)"
      open={open}
      onCancel={handleClose}
      width={960}
      destroyOnHidden
      footer={
        phase === 'upload'
          ? [
              <Button key="cancel" onClick={handleClose}>
                Anulează
              </Button>,
            ]
          : [
              <Button key="back" disabled={phase === 'importing'} onClick={resetToUpload}>
                Înapoi
              </Button>,
              <Button key="cancel" disabled={phase === 'importing'} onClick={handleClose}>
                Anulează
              </Button>,
              <Button
                key="import"
                type="primary"
                disabled={importable === 0}
                loading={commitMutation.isPending}
                onClick={() => commitMutation.mutate()}
              >
                Importă {importable} document{importable === 1 ? '' : 'e'}
              </Button>,
            ]
      }
    >
      {phase === 'upload' ? (
        <Spin spinning={previewMutation.isPending} tip="Se analizează raportul…">
          <Dragger
            accept=".xls,.xlsx"
            maxCount={1}
            showUploadList={false}
            beforeUpload={(uploaded: RcFile) => {
              previewMutation.mutate(uploaded);
              return false;
            }}
            style={{ padding: 24 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Trage raportul aici sau apasă pentru a-l selecta</p>
            <p className="ant-upload-hint">
              Export SmartBill „Raport documente furnizor" (.xls) — pentru cheltuieli mai vechi de 60 de zile, neacoperite de
              sincronizarea SPV.
            </p>
          </Dragger>
        </Spin>
      ) : (
        <Spin spinning={phase === 'importing'} tip="Se importă…">
          {preview && (
            <>
              <Alert
                style={{ marginBottom: 12 }}
                type="warning"
                showIcon
                message={`Se importă în firma: ${preview.company.name} (CIF ${preview.company.cui}).`}
                description={
                  markAllPaid
                    ? `${preview.summary.rows} documente în raport — toate se importă ca plătite; ${preview.summary.duplicates} deja importate (se sar).`
                    : `${preview.summary.rows} documente în raport: ${preview.summary.paid} plătite, ${preview.summary.unpaid} neplătite, ${preview.summary.duplicates} deja importate (se sar).`
                }
              />
              <Checkbox
                checked={markAllPaid}
                onChange={(event) => setMarkAllPaid(event.target.checked)}
                style={{ marginBottom: 12 }}
              >
                Importă toate ca plătite (ignoră statusul „Depasita" din raport)
              </Checkbox>
              <Table<SmartBillPreviewRow>
                rowKey={(row, index) => `${row.docLabel}-${index}`}
                size="small"
                columns={columns}
                dataSource={preview.rows}
                pagination={false}
                scroll={{ y: 420 }}
                rowClassName={(row) => (row.duplicate ? 'bank-import-row-disabled' : '')}
              />
            </>
          )}
        </Spin>
      )}
    </Modal>
  );
}
